import { Artifact } from "../../types";
import React, { useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Alert,
  Divider,
  CircularProgress,
  Button,
} from "@mui/material";

// Icons
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
  getAgentRunArtifacts,
  API_BASE,
  getAuthToken,
} from "../../api/agentApi";

// Set workerSrc for react-pdf to local public/pdf.worker.js (plain string, avoids process.env lint error)
// Set workerSrc for react-pdf to ES module worker (pdf.worker.mjs) in public directory
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.mjs";

interface ArtifactsTabProps {
  run: any;
  onDownload: (event: React.MouseEvent, artifact: Artifact) => void;
}

const ArtifactsTabContent: React.FC<ArtifactsTabProps> = ({
  run,
  onDownload,
}) => {
  const [previewArtifact, setPreviewArtifact] = useState<Artifact>();
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [otherFilesError, setOtherFilesError] = useState(false);
  const [images, setImages] = useState<Artifact[]>([]);
  const [pdfs, setPdfs] = useState<Artifact[]>([]);
  const [otherFiles, setOtherFiles] = useState<Artifact[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getFileType = (fileName: any) => {
    if (!fileName || typeof fileName !== "string") {
      return "other";
    }

    // Exclude system/log files
    const excludedFiles = ["console.out", "prompt.txt", "run.out", "timestamp.txt"];
    if (excludedFiles.includes(fileName.toLowerCase())) {
      return "excluded";
    }

    // Convert to lowercase and split by '.'
    const parts = fileName.toLowerCase().split(".");
    // Get the last part (the extension)
    const extension: any = parts.length > 1 ? parts.pop() : "";

    if (["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(extension)) {
      return "image";
    } else if (extension === "pdf") {
      return "pdf";
    } else {
      return "other";
    }
  };

  const getArtifactPath = React.useCallback((fullPath: any) => {
    // Ensure proper URL construction without double slashes
    const baseUrl = API_BASE.endsWith("/") ? API_BASE.slice(0, -1) : API_BASE;
    const path = fullPath.startsWith("/") ? fullPath : `/${fullPath}`;
    const combinedPath = `${baseUrl}${path}`;

    // CHECK ::: authentication when added, can lead to CORS issues. without auth does not work

    return combinedPath;
  }, []);

  useEffect(() => {
    const fetchArtifacts = async () => {
      setIsLoading(true);
      try {
        const response = await getAgentRunArtifacts(
          run.agent_id,
          run.agent_version,
          run.run_number
        );

        if (response?.artifacts) {
          response.artifacts.forEach((artifact: any) => {
            const fileType = getFileType(artifact.filename);
            artifact.type = fileType;
            const originalPath = artifact.url;
            const fullUrl = getArtifactPath(originalPath);
            artifact.url = fullUrl;
          });

          // Filter out excluded files
          const filteredArtifacts = response.artifacts.filter(
            (artifact: any) => artifact.type !== "excluded"
          );

          const imageFiles: Artifact[] = filteredArtifacts.filter(
            (file: any) => file.type === "image"
          );
          const pdfFiles: Artifact[] = filteredArtifacts.filter(
            (file: any) => file.type === "pdf"
          );
          const otherFiles: Artifact[] = filteredArtifacts.filter(
            (file: any) => file.type === "other"
          );
          setImages(imageFiles);
          setPdfs(pdfFiles);
          setOtherFiles(otherFiles);
        } else {
          console.warn("No artifacts found in response");
          setImages([]);
          setPdfs([]);
        }
      } catch (err) {
        console.error("Failed to fetch artifacts for agent run:", err);
        setImages([]);
        setPdfs([]);
        setOtherFiles([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (run?.agent_id) {
      fetchArtifacts();
    }
  }, [run.agent_id, run.agent_version, run.run_number, getArtifactPath]);

  // Reset image error when preview changes

  // Reset image error and PDF blob when preview changes
  useEffect(() => {
    setImageError(false);
    setPdfError(null);
    setOtherFilesError(false);
    setNumPages(null);
    
    // Clean up previous PDF blob URL
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    
    // If previewing a PDF, fetch it with auth and set blob URL
    if (previewArtifact && previewArtifact.type === "pdf") {
      setPdfLoading(true);
      (async () => {
        try {
          const token = getAuthToken();
          const response = await fetch(previewArtifact.url, {
            method: "GET",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (!response.ok) throw new Error("Failed to fetch PDF");
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          setPdfBlobUrl(blobUrl);
        } catch (err) {
          setPdfBlobUrl(null);
          setPdfError("Unable to preview PDF. Please download to view.");
        } finally {
          setPdfLoading(false);
        }
      })();
    }
    // Clean up blob URL on unmount
    return () => {
      if (pdfBlobUrl) {
        window.URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [previewArtifact]);

  if (previewArtifact) {
    const file = previewArtifact;
    const isImage = file.type === "image";
    const handleImageError = (event: any) => {
      console.error("Image failed to load:", event);
      console.error("Failed URL:", file.url);
      setImageError(true);
    };

    try {
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header with Back Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <IconButton onClick={() => setPreviewArtifact(undefined)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="subtitle1" fontWeight="bold">
              {file.filename} - Preview
            </Typography>
            <IconButton
              onClick={(e) => onDownload(e, file)}
              aria-label="download"
            >
              <DownloadIcon color="action" />
            </IconButton>
          </Box>

          {/* Content Area */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
          {isImage &&
            (imageError ? (
              <Box sx={{ textAlign: "center", p: 4 }}>
                <BrokenImageIcon
                  sx={{ fontSize: 80, color: "text.secondary", mb: 1 }}
                />
                <Typography variant="body1" color="text.secondary">
                  Could not load image.
                </Typography>
              </Box>
            ) : (
              <Box
                component="img"
                src={file.url}
                alt={file.filename}
                onError={handleImageError}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            ))
          }
          {file.type === "pdf" && pdfLoading && (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading PDF preview...
              </Typography>
            </Box>
          )}
          {file.type === "pdf" && pdfBlobUrl && (
            <Box sx={{ width: "100%", height: "400px", overflowY: "auto" }}>
              <Document
                file={pdfBlobUrl}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                onLoadError={(err) =>
                  setPdfError("Unable to preview PDF. Please download to view.")
                }
                loading={<CircularProgress />}
              >
                {Array.from(new Array(numPages), (el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={600}
                  />
                ))}
              </Document>
            </Box>
          )}
          {file.type === "pdf" && pdfError && (
            <Typography variant="body2" color="text.secondary">
              {pdfError}
            </Typography>
          )}

          {file.type === "other" && (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <InsertDriveFileIcon
                sx={{ fontSize: 80, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.primary" gutterBottom>
                {file.filename}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                This file type cannot be previewed. Click download to view the file.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={(e: React.MouseEvent) => onDownload(e, file)}
                sx={{ mt: 2 }}
              >
                Download File
              </Button>
            </Box>
          )}
          
        </Box>
      </Box>
      );
    } catch (error) {
      console.error("Error rendering preview artifact:", error);
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: 4,
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Preview Error
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Unable to preview {file.filename}. Please try downloading the file instead.
          </Typography>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={(e: React.MouseEvent) => onDownload(e, file)}
            sx={{ mt: 2 }}
          >
            Download File
          </Button>
          <Button
            variant="text"
            onClick={() => setPreviewArtifact(undefined)}
            sx={{ mt: 1 }}
          >
            Go Back
          </Button>
        </Box>
      );
    }
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading artifacts...
        </Typography>
      </Box>
    );
  }

  return (
    <List dense sx={{ height: "100%", px: 6}}>
      {/* Images Section */}
      <Typography
        variant="h6"
        sx={{
          mt: 1,
          mb: 1,
          position: "sticky",
          top: 0,
          zIndex: 1,
          p: "8px 0",
        }}
      >
        🖼️ Images ({images.length})
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {images.length === 0 ? (
        <Alert severity="info">No images were generated for this run.</Alert>
      ) : (
        images.map((image: Artifact) => (
          <ListItem
            key={image.id}
            secondaryAction={
              <Box>
                {/* <IconButton
                  edge="end"
                  aria-label="view"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Viewing image:', image.filename, image.url);
                    setPreviewArtifact(image);
                  }}
                  sx={{ mr: 1 }}
                >
                  <VisibilityIcon color="primary" />
                </IconButton> */}
                <IconButton
                  edge="end"
                  aria-label="download"
                  onClick={(e) => {
                    console.log(
                      "Downloading image:",
                      image.filename,
                      image.url
                    );
                    onDownload(e, image);
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Box>
            }
            disablePadding
            sx={{ borderBottom: "1px solid #eee" }}
          >
            <ListItemIcon>
              {image.type === "image" ? (
                <ImageOutlinedIcon color="primary" />
              ) : (
                <PictureAsPdfOutlinedIcon color="error" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={image.filename}
              secondary={`Type: ${image.type}`}
            />
          </ListItem>
        ))
      )}
      {/* PDFs Section */}
      <Typography
        variant="h6"
        sx={{
          mt: 3,
          mb: 1,
          position: "sticky",
          top: 0,
          zIndex: 1,
          p: "8px 0",
        }}
      >
        📄 PDFs ({pdfs.length})
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {pdfs.length === 0 ? (
        <Alert severity="info">No PDFs were generated for this run.</Alert>
      ) : (
        pdfs.map((pdf: Artifact) => (
          <ListItem
            key={pdf.id}
            secondaryAction={
              <Box>
                {/* <IconButton
                  edge="end"
                  aria-label="view"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Viewing PDF:', pdf.filename, pdf.url);
                    setPreviewArtifact(pdf);
                  }}
                  sx={{ mr: 1 }}
                >
                  <VisibilityIcon color="primary" />
                </IconButton> */}
                <IconButton
                  edge="end"
                  aria-label="download"
                  onClick={(e) => {
                    console.log("Downloading PDF:", pdf.filename, pdf.url);
                    onDownload(e, pdf);
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Box>
            }
            disablePadding
            sx={{ borderBottom: "1px solid #eee" }}
          >
            <ListItemIcon>
              {pdf.type === "image" ? (
                <ImageOutlinedIcon color="primary" />
              ) : (
                <PictureAsPdfOutlinedIcon color="error" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={pdf.filename}
              secondary={`Type: ${pdf.type}`}
            />
          </ListItem>
        ))
      )}
      
      {/* Other Files Section */}
      <Typography
        variant="h6"
        sx={{
          mt: 3,
          mb: 1,
          position: "sticky",
          top: 0,
          zIndex: 1,
          p: "8px 0",
        }}
      >
        📁 Other Files ({otherFiles.length})
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {otherFiles.length === 0 ? (
        <Alert severity="info">No other files were generated for this run.</Alert>
      ) : (
        otherFiles.map((file: Artifact) => (
          <ListItem
            key={file.id}
            secondaryAction={
              <Box>
                <IconButton
                  edge="end"
                  aria-label="download"
                  onClick={(e) => {
                    console.log("Downloading other file:", file.filename, file.url);
                    onDownload(e, file);
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Box>
            }
            disablePadding
            sx={{ borderBottom: "1px solid #eee" }}
          >
            <ListItemIcon>
              <InsertDriveFileIcon color="action" />
            </ListItemIcon>
            <ListItemText
              primary={file.filename}
              secondary={`Type: ${file.type}`}
            />
          </ListItem>
        ))
      )}
    </List>
  );
};

export default ArtifactsTabContent;
